import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    this.image = this.element.querySelector("img")
    if (!this.image) return

    this.image.style.cursor = "zoom-in"
    this._handleImageClick = this.openOverlay.bind(this)
    this.image.addEventListener("click", this._handleImageClick)
  }

  disconnect() {
    if (this.image) {
      this.image.removeEventListener("click", this._handleImageClick)
    }
    this.closeOverlay()
  }

  openOverlay() {
    if (this.overlay) return
    // Nothing to zoom into while the image is still loading or has failed;
    // image-loader is showing a skeleton or the error panel in its place.
    if (this.image.naturalWidth === 0) return

    this.scale = 1
    this.translateX = 0
    this.translateY = 0
    this.initialPinchDistance = null
    this.initialScale = 1
    this.isPanning = false
    this.panStartX = 0
    this.panStartY = 0
    this.initialTranslateX = 0
    this.initialTranslateY = 0
    this.lastTapTime = 0
    this.lastTapX = 0
    this.lastTapY = 0

    this.overlay = document.createElement("div")
    this.overlay.style.cssText =
      "position:fixed;inset:0;z-index:50;display:flex;align-items:center;justify-content:center;" +
      "background:rgba(0,0,0,0.92);touch-action:none;user-select:none;"

    const closeBtn = document.createElement("button")
    closeBtn.style.cssText =
      "position:absolute;top:1rem;right:1rem;z-index:10;color:rgba(255,255,255,0.8);" +
      "width:2.5rem;height:2.5rem;display:flex;align-items:center;justify-content:center;" +
      "border-radius:9999px;background:rgba(0,0,0,0.3);border:none;cursor:pointer;"
    closeBtn.innerHTML = '<i class="bi bi-x-lg" style="font-size:1.125rem;"></i>'
    closeBtn.addEventListener("click", (e) => {
      e.stopPropagation()
      this.closeOverlay()
    })

    this.imageContainer = document.createElement("div")
    this.imageContainer.style.cssText =
      "transform-origin:center center;will-change:transform;touch-action:none;"

    this.zoomImage = document.createElement("img")
    this.zoomImage.src = this.image.src
    this.zoomImage.alt = this.image.alt
    this.zoomImage.draggable = false
    this.zoomImage.style.cssText =
      "max-width:100vw;max-height:100vh;object-fit:contain;pointer-events:none;"

    this.imageContainer.appendChild(this.zoomImage)
    this.overlay.appendChild(closeBtn)
    this.overlay.appendChild(this.imageContainer)
    document.body.appendChild(this.overlay)
    document.body.classList.add("overflow-hidden")

    this._boundTouchStart = this._onTouchStart.bind(this)
    this._boundTouchMove = this._onTouchMove.bind(this)
    this._boundTouchEnd = this._onTouchEnd.bind(this)
    this._boundKeydown = this._onKeydown.bind(this)
    this._boundBackdropClick = this._onBackdropClick.bind(this)
    this._boundWheel = this._onWheel.bind(this)
    this._boundMouseDown = this._onMouseDown.bind(this)
    this._boundMouseMove = this._onMouseMove.bind(this)
    this._boundMouseUp = this._onMouseUp.bind(this)

    this.overlay.addEventListener("touchstart", this._boundTouchStart, { passive: false })
    this.overlay.addEventListener("touchmove", this._boundTouchMove, { passive: false })
    this.overlay.addEventListener("touchend", this._boundTouchEnd)
    this.overlay.addEventListener("click", this._boundBackdropClick)
    this.overlay.addEventListener("wheel", this._boundWheel, { passive: false })
    this.overlay.addEventListener("mousedown", this._boundMouseDown)
    document.addEventListener("mousemove", this._boundMouseMove)
    document.addEventListener("mouseup", this._boundMouseUp)
    document.addEventListener("keydown", this._boundKeydown)
  }

  closeOverlay() {
    if (!this.overlay) return
    this.overlay.remove()
    this.overlay = null
    this.zoomImage = null
    this.imageContainer = null
    document.body.classList.remove("overflow-hidden")
    document.removeEventListener("keydown", this._boundKeydown)
    document.removeEventListener("mousemove", this._boundMouseMove)
    document.removeEventListener("mouseup", this._boundMouseUp)
  }

  // Touch events

  _onTouchStart(e) {
    if (e.touches.length === 2) {
      e.preventDefault()
      this.isPanning = false
      this.initialPinchDistance = this._getTouchDistance(e.touches)
      this.initialScale = this.scale
    } else if (e.touches.length === 1) {
      this._handleDoubleTap(e)
      if (this.scale > 1) {
        this.isPanning = true
        this.panStartX = e.touches[0].clientX
        this.panStartY = e.touches[0].clientY
        this.initialTranslateX = this.translateX
        this.initialTranslateY = this.translateY
      }
    }
  }

  _onTouchMove(e) {
    e.preventDefault()

    if (e.touches.length === 2 && this.initialPinchDistance) {
      const currentDistance = this._getTouchDistance(e.touches)
      const ratio = currentDistance / this.initialPinchDistance
      this.scale = this._clampScale(this.initialScale * ratio)
      if (this.scale <= 1) {
        this.translateX = 0
        this.translateY = 0
      }
      this._applyTransform()
    } else if (e.touches.length === 1 && this.isPanning && this.scale > 1) {
      const deltaX = e.touches[0].clientX - this.panStartX
      const deltaY = e.touches[0].clientY - this.panStartY
      this.translateX = this.initialTranslateX + deltaX
      this.translateY = this.initialTranslateY + deltaY
      ;[this.translateX, this.translateY] = this._clampTranslation(this.translateX, this.translateY)
      this._applyTransform()
    }
  }

  _onTouchEnd(e) {
    if (e.touches.length < 2) {
      this.initialPinchDistance = null
    }
    if (e.touches.length === 0) {
      this.isPanning = false
      if (this.scale < 1.05) {
        this.scale = 1
        this.translateX = 0
        this.translateY = 0
        this._animateTransform()
      }
    }
  }

  _handleDoubleTap(e) {
    const now = Date.now()
    const touch = e.touches[0]
    const dx = touch.clientX - this.lastTapX
    const dy = touch.clientY - this.lastTapY
    const distance = Math.sqrt(dx * dx + dy * dy)

    if (now - this.lastTapTime < 300 && distance < 30) {
      e.preventDefault()
      if (this.scale > 1) {
        this.scale = 1
        this.translateX = 0
        this.translateY = 0
      } else {
        this.scale = 2
        const centerX = window.innerWidth / 2
        const centerY = window.innerHeight / 2
        this.translateX = centerX - touch.clientX
        this.translateY = centerY - touch.clientY
        ;[this.translateX, this.translateY] = this._clampTranslation(this.translateX, this.translateY)
      }
      this._animateTransform()
      this.lastTapTime = 0
      this.isPanning = false
      return
    }

    this.lastTapTime = now
    this.lastTapX = touch.clientX
    this.lastTapY = touch.clientY
  }

  // Mouse events (desktop)

  _onWheel(e) {
    e.preventDefault()
    const delta = e.deltaY > 0 ? 0.9 : 1.1
    this.scale = this._clampScale(this.scale * delta)
    if (this.scale <= 1) {
      this.translateX = 0
      this.translateY = 0
    }
    this._applyTransform()
  }

  _onMouseDown(e) {
    if (this.scale > 1) {
      this.isPanning = true
      this.panStartX = e.clientX
      this.panStartY = e.clientY
      this.initialTranslateX = this.translateX
      this.initialTranslateY = this.translateY
      this.imageContainer.style.cursor = "grabbing"
    }
  }

  _onMouseMove(e) {
    if (!this.isPanning) return
    const dx = e.clientX - this.panStartX
    const dy = e.clientY - this.panStartY
    this.translateX = this.initialTranslateX + dx
    this.translateY = this.initialTranslateY + dy
    ;[this.translateX, this.translateY] = this._clampTranslation(this.translateX, this.translateY)
    this._applyTransform()
  }

  _onMouseUp() {
    this.isPanning = false
    if (this.imageContainer) {
      this.imageContainer.style.cursor = this.scale > 1 ? "grab" : ""
    }
  }

  _onBackdropClick(e) {
    if (e.target === this.overlay && this.scale <= 1) {
      this.closeOverlay()
    }
  }

  _onKeydown(e) {
    if (e.key === "Escape") {
      this.closeOverlay()
    }
  }

  // Geometry helpers

  _getTouchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  _clampScale(scale) {
    return Math.min(Math.max(scale, 1), 4)
  }

  _clampTranslation(x, y) {
    if (!this.zoomImage) return [0, 0]

    const baseWidth = this.zoomImage.offsetWidth
    const baseHeight = this.zoomImage.offsetHeight
    const scaledWidth = baseWidth * this.scale
    const scaledHeight = baseHeight * this.scale

    const maxX = Math.max(0, (scaledWidth - window.innerWidth) / 2)
    const maxY = Math.max(0, (scaledHeight - window.innerHeight) / 2)

    return [
      Math.min(Math.max(x, -maxX), maxX),
      Math.min(Math.max(y, -maxY), maxY)
    ]
  }

  _applyTransform() {
    if (!this.imageContainer) return
    this.imageContainer.style.transition = "none"
    this.imageContainer.style.transform =
      `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`
  }

  _animateTransform() {
    if (!this.imageContainer) return
    this.imageContainer.style.transition = "transform 0.2s ease-out"
    this.imageContainer.style.transform =
      `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`
    setTimeout(() => {
      if (this.imageContainer) {
        this.imageContainer.style.transition = "none"
      }
    }, 200)
  }
}
